import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { FunnelIcon, Squares2X2Icon } from '@/components/icons';

import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Loading } from '@/components/Loading';
import { TicketListView } from '@/components/TicketListView';
import { CreateTicketModal } from '@/components/tickets';
import { FilterSidebar, useAdvancedFilters } from '@/components/filtering';
import { UserRole, TicketStatus, TicketPriority, TicketSource } from '@/types/api';
import { useTicketViewPreferences } from '@/utils/ticketViewPreferences';
import { useSignalR, usePresence } from '@/hooks/useSignalR';
import { ticketService } from '@/services/ticketService';
import { exportService } from '@/services/exportService';
import type { TicketViewPreferences } from '@/types/ui';
import type { AdvancedFilters } from '@/types/filters';
import type { Ticket, PaginatedResponse, TicketQueryParams } from '@/types/api';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const filterDate = (value: Date | string | undefined, endOfDay = false): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  if (endOfDay) date.setHours(23, 59, 59, 999);
  return date.toISOString();
};

/**
 * Enhanced Ticket List Page Component
 * 
 * Features comprehensive ticket management with:
 * - Advanced filtering sidebar with Freshdesk-style interface
 * - Multi-view layouts (Card, Table, Inbox)
 * - Saved filter views and presets
 * - Real-time filter chips and search
 * - Multi-select bulk operations
 * - Persistent preferences and URL synchronization
 * - Professional Webcoda-styled interface
 */
export function TicketList() {
  useDocumentTitle('Support Tickets | SupportDesk');
  const { user, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // View preferences with localStorage persistence
  const { preferences, setPreferences } = useTicketViewPreferences();

  // Check for query string parameter to force view type (debugging only)
  const urlParams = new URLSearchParams(location.search);
  const forceView = urlParams.get('view');
  const requesterId = urlParams.get('requester') || undefined;

  // Override preferences with query string parameter if provided
  const currentPreferences = useMemo(() => {
    if (forceView && ['table', 'card', 'inbox'].includes(forceView)) {
      return {
        ...preferences,
        viewType: forceView as 'table' | 'card' | 'inbox'
      };
    }
    return preferences;
  }, [preferences, forceView]);
  
  // Memoize callbacks to prevent useAdvancedFilters from re-initializing
  const onFiltersChange = useCallback((_newFilters: AdvancedFilters) => {
    setPagination(prev => prev.pageNumber === 1 ? prev : { ...prev, pageNumber: 1 });
    setRefreshKey(prev => prev + 1);
  }, []);

  // Advanced filtering system
  const {
    filters,
    updateFilters,
    clearAllFilters,
    removeFilter,
    activeFilters,
    sidebarExpanded,
    toggleSidebar,
    savedViews,
    currentViewId,
    saveView,
    loadView,
    deleteView,
    setDefaultView,
    savedViewsLoading,
    savedViewsError,
    filterOptions,
    loadFilterOptions,
    loading: filterLoading
  } = useAdvancedFilters({
    userId: user?.id || '',
    userRole: user?.role || UserRole.EndUser,
    enableUrlSync: true,
    enableLocalStorage: true,
    onFiltersChange
  });
  
  // State management
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showClosedResolved, setShowClosedResolved] = useState(false);
  const [flashMessage, setFlashMessage] = useState<{ type: 'success' | 'info' | 'error'; message: string } | null>(null);

  // Sort state for server-side sorting
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: 'updatedat',
    direction: 'desc'
  });

  // Handle flash message passed via navigation state (e.g., after closing a ticket)
  React.useEffect(() => {
    const state: any = location.state;
    if (state?.flash) {
      setFlashMessage(state.flash);
      // Clear flash from history state
      navigate(location.pathname, { replace: true, state: { ...(state.returnState || {}), from: state.from } });
      const t = setTimeout(() => setFlashMessage(null), 3000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [location.state, location.pathname]); // Remove navigate from dependencies

  // SignalR integration for real-time updates
  useSignalR({
    onRealTimeEvent: (event) => {
      if (event.eventType === 'TicketUpdated' || event.eventType === 'TicketCreated') {
        // Refresh tickets when real-time updates are received
        handleRefresh();
      }
    }
  });
  
  // Update user presence
  usePresence();

  // Fetch tickets from API
  const fetchTickets = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Build query parameters from filters
      // Default: hide Resolved and Closed (and Cancelled) unless user selects otherwise
      const defaultStatuses = [
        TicketStatus.New,
        TicketStatus.Open,
        TicketStatus.Pending,
        TicketStatus.OnHold,
      ];

      const queryParams: TicketQueryParams = {
        page: pagination.pageNumber,
        pageSize: pagination.pageSize,
        searchTerm: filters.search || undefined,
        // Use selected statuses if any, otherwise fall back to default logic
        statuses: filters.statuses.length > 0 ? filters.statuses :
               (showClosedResolved ? undefined : defaultStatuses),
        priorities: filters.priorities.length > 0 ? filters.priorities : undefined,
        sources: filters.sources.length > 0 ? filters.sources : undefined,
        onlyMyTickets: filters.myTicketsOnly || undefined,
        onlyUnassigned: filters.unassignedOnly || undefined,
        assignedAgentIds: filters.assignedAgents.length > 0 ? filters.assignedAgents : undefined,
        companyIds: filters.companies.length > 0 ? filters.companies : undefined,
        requesterIds: requesterId ? [requesterId] : undefined,
        tags: filters.tags.length > 0 ? filters.tags : undefined,
        createdAfter: filterDate(filters.createdDateRange?.from),
        createdBefore: filterDate(filters.createdDateRange?.to, true),
        updatedAfter: filterDate(filters.updatedDateRange?.from),
        updatedBefore: filterDate(filters.updatedDateRange?.to, true),
        hasAttachments: filters.hasAttachments === true ? true : undefined,
        isEscalated: filters.escalated === true ? true : undefined,
        sortBy: sortConfig.key || 'updatedat',
        sortDirection: sortConfig.direction
      };

      // For end users, ensure they only see their own tickets
      if (user?.role === UserRole.EndUser) {
        queryParams.requesterIds = [user.id];
      }

      const response = await ticketService.getTickets(queryParams);

      if (response.success && response.data) {
        setTickets(response.data.data);
        setPagination({
          pageNumber: response.data.pageNumber,
          pageSize: response.data.pageSize,
          totalCount: response.data.totalCount,
          totalPages: response.data.totalPages,
          hasNextPage: response.data.hasNextPage,
          hasPreviousPage: response.data.hasPreviousPage,
        });
      } else {
        throw new Error(response.message || 'Failed to fetch tickets');
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
      setTickets([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.pageNumber, pagination.pageSize, user?.id, user?.role, showClosedResolved, sortConfig, requesterId, refreshKey]);

  // Effect to fetch tickets whenever fetchTickets dependencies change (page, filters, sorting, refresh)
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);


  // Event handlers
  const handleTicketClick = useCallback((ticket: Ticket) => {
    // Generate breadcrumb context based on current filters and view
    const generateBreadcrumbContext = () => {
      // Check for active search
      if (filters.search) {
        return {
          type: 'search',
          query: filters.search,
          value: filters.search
        };
      }
      
      // Check for my tickets filter
      if (filters.myTicketsOnly) {
        return {
          type: 'myTickets',
          value: 'myTickets'
        };
      }
      
      // Check for unassigned filter
      if (filters.unassignedOnly) {
        return {
          type: 'unassignedTickets',
          value: 'unassigned'
        };
      }
      
      // Check for company filter
      if (filters.companies.length === 1 && filterOptions.companies.length > 0) {
        const company = filterOptions.companies.find(c => c.companyId === filters.companies[0]);
        return {
          type: 'companyFilter',
          value: filters.companies[0],
          companyName: company?.companyName
        };
      }
      
      // Check for single status filter
      if (filters.statuses.length === 1) {
        return {
          type: 'statusFilter',
          value: filters.statuses[0]
        };
      }
      
      // Check for single priority filter
      if (filters.priorities.length === 1) {
        return {
          type: 'priorityFilter',
          value: filters.priorities[0]
        };
      }
      
      // Check for single tag filter
      if (filters.tags.length === 1) {
        return {
          type: 'tagFilter',
          value: filters.tags[0],
          tagName: filters.tags[0]
        };
      }
      
      // Check for overdue filter
      if (filters.isOverdue) {
        return {
          type: 'overdue',
          value: 'overdue'
        };
      }
      
      // Check for VIP customers filter
      if (filters.vipCustomersOnly) {
        return {
          type: 'vipCustomers',
          value: 'vip'
        };
      }
      
      // Check for current saved view
      if (currentViewId) {
        const currentView = savedViews.find(view => view.id === currentViewId);
        return {
          type: 'savedView',
          value: currentViewId,
          viewName: currentView?.name
        };
      }
      
      // Check for date filter
      if (filters.createdDateRange?.preset) {
        const dateLabels = {
          'today': 'Today',
          'yesterday': 'Yesterday', 
          'thisWeek': 'This Week',
          'lastWeek': 'Last Week',
          'thisMonth': 'This Month',
          'lastMonth': 'Last Month',
          'last7Days': 'Last 7 Days',
          'last30Days': 'Last 30 Days',
          'last90Days': 'Last 90 Days'
        };
        return {
          type: 'dateFilter',
          value: filters.createdDateRange.preset,
          label: dateLabels[filters.createdDateRange.preset as keyof typeof dateLabels] || 'Date Filtered'
        };
      }
      
      // Check if we have multiple active filters
      if (activeFilters.length > 0) {
        const filterLabels = activeFilters.map(f => f.displayValue).join(', ');
        return {
          type: 'customFilter',
          value: 'multiple',
          label: activeFilters.length > 2 ? `${activeFilters.length} Filters` : filterLabels
        };
      }
      
      // Default to all tickets
      return null;
    };
    
    const context = generateBreadcrumbContext();
    
    navigate(`/tickets/${ticket.ticketNumber || ticket.id}`, {
      state: {
        from: location.pathname + location.search,
        context,
        returnState: {
          filters,
          selectedTickets,
          pagination
        }
      }
    });
  }, [navigate, filters, filterOptions, currentViewId, savedViews, activeFilters, location, selectedTickets, pagination]);

  const handleTicketSelect = useCallback((ticketIds: string[]) => {
    setSelectedTickets(ticketIds);
  }, []);

  const handleStatusChange = useCallback(async (ticketId: string, status: string) => {
    try {
      setError(null);
      const response = await ticketService.updateTicketStatus(ticketId, status as TicketStatus);
      
      if (response.success && response.data) {
        // Update local state - let the backend provide the correct updatedAt timestamp
        setTickets(prev => prev.map(ticket => 
          ticket.id === ticketId 
            ? { ...ticket, status: response.data.status, updatedAt: response.data.updatedAt }
            : ticket
        ));
      } else {
        throw new Error(response.message || 'Failed to update ticket status');
      }
    } catch (err) {
      console.error('Failed to update ticket status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update ticket status');
    }
  }, []);

  const handlePriorityChange = useCallback(async (ticketId: string, priority: string) => {
    try {
      setError(null);
      const response = await ticketService.updateTicketPriority(ticketId, priority as TicketPriority);
      
      if (response.success && response.data) {
        // Update local state - let the backend provide the correct updatedAt timestamp
        setTickets(prev => prev.map(ticket => 
          ticket.id === ticketId 
            ? { ...ticket, priority: response.data.priority, updatedAt: response.data.updatedAt }
            : ticket
        ));
      } else {
        throw new Error(response.message || 'Failed to update ticket priority');
      }
    } catch (err) {
      console.error('Failed to update ticket priority:', err);
      setError(err instanceof Error ? err.message : 'Failed to update ticket priority');
    }
  }, []);

  const handleAssignmentChange = useCallback(async (ticketId: string, agentId: string | null) => {
    try {
      setError(null);
      const response = await ticketService.assignTicket(ticketId, agentId);
      
      if (response.success && response.data) {
        // Update local state - let the backend provide the correct updatedAt timestamp
        setTickets(prev => prev.map(ticket => 
          ticket.id === ticketId 
            ? { ...ticket, assignedToId: response.data.assignedToId, assignedTo: response.data.assignedTo, updatedAt: response.data.updatedAt }
            : ticket
        ));
      } else {
        throw new Error(response.message || 'Failed to assign ticket');
      }
    } catch (err) {
      console.error('Failed to assign ticket:', err);
      setError(err instanceof Error ? err.message : 'Failed to assign ticket');
    }
  }, []);

  // Bulk operation handlers
  const handleBulkStatusChange = useCallback(async (status: string) => {
    if (selectedTickets.length === 0) return;
    try {
      setError(null);
      const response = await ticketService.bulkUpdateStatus(selectedTickets, status);
      if (!response.success) throw new Error(response.message || 'Failed bulk status update');
      setSelectedTickets([]);
      fetchTickets();
    } catch (err) {
      console.error('Failed bulk status update:', err);
      setError(err instanceof Error ? err.message : 'Failed bulk status update');
    }
  }, [selectedTickets, fetchTickets]);

  const handleBulkPriorityChange = useCallback(async (priority: string) => {
    if (selectedTickets.length === 0) return;
    try {
      setError(null);
      const response = await ticketService.bulkUpdatePriority(selectedTickets, priority);
      if (!response.success) throw new Error(response.message || 'Failed bulk priority update');
      setSelectedTickets([]);
      fetchTickets();
    } catch (err) {
      console.error('Failed bulk priority update:', err);
      setError(err instanceof Error ? err.message : 'Failed bulk priority update');
    }
  }, [selectedTickets, fetchTickets]);

  const handleBulkAssign = useCallback(async (agentId: string) => {
    if (selectedTickets.length === 0) return;
    try {
      setError(null);
      const response = await ticketService.bulkAssign(selectedTickets, agentId);
      if (!response.success) throw new Error(response.message || 'Failed bulk assign');
      setSelectedTickets([]);
      fetchTickets();
    } catch (err) {
      console.error('Failed bulk assign:', err);
      setError(err instanceof Error ? err.message : 'Failed bulk assign');
    }
  }, [selectedTickets, fetchTickets]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, pageNumber: page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize, pageNumber: 1 }));
  }, []);

  const handleViewPreferencesChange = useCallback((newPreferences: TicketViewPreferences) => {
    setPreferences(newPreferences);
  }, [setPreferences]);

  // Handle sort changes for server-side sorting
  const handleSortChange = useCallback((key: string) => {
    // Prevent sorting while loading to avoid rapid clicks
    if (isLoading) return;

    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, [isLoading]);

  // Handle ticket creation success
  const handleTicketCreated = useCallback((newTicket: Ticket) => {
    // Add the new ticket to the beginning of the list
    setTickets(prev => [newTicket, ...prev]);
    
    // Update pagination count
    setPagination(prev => ({
      ...prev,
      totalCount: prev.totalCount + 1
    }));

    // Navigate to the new ticket with context
    navigate(`/tickets/${newTicket.id}`, {
      state: {
        from: location.pathname + location.search,
        context: {
          type: 'newTicket',
          value: 'created'
        },
        returnState: {
          filters,
          selectedTickets,
          pagination
        }
      }
    });
  }, [navigate, location, filters, selectedTickets, pagination]);

  // Handle export tickets
  const handleExportTickets = useCallback(async () => {
    try {
      setError(null);
      
      // Convert current filters to export options
      const exportOptions = {
        statuses: filters.statuses.length > 0 ? filters.statuses :
          (showClosedResolved ? undefined : [TicketStatus.New, TicketStatus.Open, TicketStatus.Pending, TicketStatus.OnHold]),
        priorities: filters.priorities.length > 0 ? filters.priorities : undefined,
        sources: filters.sources.length > 0 ? filters.sources : undefined,
        assignedAgentIds: filters.assignedAgents.length > 0 ? filters.assignedAgents : undefined,
        companyIds: filters.companies.length > 0 ? filters.companies : undefined,
        requesterIds: requesterId ? [requesterId] : undefined,
        tags: filters.tags.length > 0 ? filters.tags : undefined,
        onlyMyTickets: filters.myTicketsOnly || undefined,
        onlyUnassigned: filters.unassignedOnly || undefined,
        searchTerm: filters.search || undefined,
        createdAfter: filterDate(filters.createdDateRange?.from),
        createdBefore: filterDate(filters.createdDateRange?.to, true),
        updatedAfter: filterDate(filters.updatedDateRange?.from),
        updatedBefore: filterDate(filters.updatedDateRange?.to, true),
        hasAttachments: filters.hasAttachments === true ? true : undefined,
        isEscalated: filters.escalated === true ? true : undefined,
        sortBy: sortConfig.key || 'updatedat',
        sortDirection: sortConfig.direction,
      };

      await exportService.exportTickets(exportOptions);
    } catch (err) {
      console.error('Failed to export tickets:', err);
      setError(err instanceof Error ? err.message : 'Failed to export tickets');
    }
  }, [filters, requesterId, showClosedResolved, sortConfig]);

  const getPageTitle = () => {
    switch (user?.role) {
      case UserRole.Admin:
        return 'All Support Tickets';
      case UserRole.Agent:
        return 'Support Tickets';
      case UserRole.EndUser:
        return 'My Support Tickets';
      default:
        return 'Support Tickets';
    }
  };

  const getPageDescription = () => {
    const selectedCount = selectedTickets.length;
    const totalCount = pagination.totalCount;
    
    if (selectedCount > 0) {
      return `${selectedCount} of ${totalCount} ticket${totalCount !== 1 ? 's' : ''} selected`;
    }
    
    return `${totalCount} ticket${totalCount !== 1 ? 's' : ''} found`;
  };

  if (!user) {
    return (
      <div className="min-h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex relative">
      {/* Main Content Area */}
      <div
        className={clsx(
          "flex-1 flex flex-col overflow-hidden transition-all duration-300",
          sidebarExpanded ? "mr-80" : "mr-0"
        )}
      >
        {/* Page Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="py-6 md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                    {getPageTitle()}
                  </h1>
                  

                  {/* Quick toggle for Closed/Resolved */}
                  <div className="ml-4 flex items-center">
                    <label className="inline-flex items-center space-x-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={showClosedResolved}
                        onChange={(e) => {
                          setShowClosedResolved(e.target.checked);
                          setPagination(prev => prev.pageNumber === 1 ? prev : { ...prev, pageNumber: 1 });
                          setRefreshKey((k) => k + 1);
                        }}
                      />
                      <span>Show Resolved/Closed</span>
                    </label>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  {getPageDescription()}
                </p>
                {requesterId && user?.role !== UserRole.EndUser && (
                  <div className="mt-2 flex items-center gap-3 text-sm text-gray-700">
                    <span>Showing tickets for the selected customer</span>
                    <Button variant="ghost" size="sm" onClick={() => {
                      const nextParams = new URLSearchParams(location.search);
                      nextParams.delete('requester');
                      navigate({ pathname: location.pathname, search: nextParams.toString() });
                    }}>Show all customers</Button>
                  </div>
                )}
              </div>
              <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
                <Button
                  variant="outline"
                  onClick={toggleSidebar}
                  className={clsx(
                    'flex items-center',
                    sidebarExpanded && 'bg-primary-50 text-primary-700 border-primary-200'
                  )}
                >
                  <FunnelIcon className="w-4 h-4 mr-2" />
                  Filters
                  {activeFilters.length > 0 && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      {activeFilters.length}
                    </span>
                  )}
                </Button>
                {hasAnyRole([UserRole.Agent, UserRole.Manager, UserRole.Admin]) && (
                  <Button
                    variant="outline"
                    onClick={handleExportTickets}
                    disabled={isLoading}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    </svg>
                    Export CSV
                  </Button>
                )}
                <Button 
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Ticket
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {/* Error Display */}
          {error && (
            <Card>
              <div className="px-6 py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                  <div className="ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefresh}
                    >
                      Retry
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Enhanced Ticket List View */}
          <TicketListView
            tickets={tickets}
            loading={isLoading || filterLoading}
            selectedTickets={selectedTickets}
            onTicketSelect={handleTicketSelect}
            onTicketClick={handleTicketClick}
            onStatusChange={handleStatusChange}
            onPriorityChange={handlePriorityChange}
            onAssignmentChange={handleAssignmentChange}
            onBulkStatusChange={handleBulkStatusChange}
            onBulkPriorityChange={handleBulkPriorityChange}
            onBulkAssign={handleBulkAssign}
            viewPreferences={currentPreferences}
            onViewPreferencesChange={handleViewPreferencesChange}
            bulkActionsEnabled={hasAnyRole([UserRole.Admin, UserRole.Agent])}
            showQuickActions={true}
            sortConfig={sortConfig}
            onSortChange={handleSortChange}
          />

          {/* Real Pagination */}
          {tickets.length > 0 && (
            <Card>
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing {((pagination.pageNumber - 1) * pagination.pageSize) + 1} to {Math.min(pagination.pageNumber * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} tickets
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      disabled={!pagination.hasPreviousPage || isLoading}
                      onClick={() => handlePageChange(pagination.pageNumber - 1)}
                    >
                      Previous
                    </Button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter(pageNum => {
                        const start = Math.max(1, Math.min(pagination.pageNumber - 2, pagination.totalPages - 4));
                        const end = Math.min(pagination.totalPages, start + 4);
                        return pageNum >= start && pageNum <= end;
                      })
                      .map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={isLoading}
                          className={`px-3 py-1 text-sm font-medium rounded ${
                            pageNum === pagination.pageNumber
                              ? 'bg-primary-50 text-primary-600 font-semibold'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      ))}

                    <Button
                      variant="outline"
                      disabled={!pagination.hasNextPage || isLoading}
                      onClick={() => handlePageChange(pagination.pageNumber + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Advanced Filter Sidebar */}
      <FilterSidebar
        filters={filters}
        onFiltersChange={updateFilters}
        expanded={sidebarExpanded}
        onToggle={toggleSidebar}
        savedViews={savedViews}
        currentViewId={currentViewId}
        onSaveView={saveView}
        onLoadView={loadView}
        onDeleteView={deleteView}
        onSetDefaultView={setDefaultView}
        filterOptions={filterOptions}
        onLoadFilterOptions={loadFilterOptions}
        position="right"
        width={320}
        overlay={false}
        userRole={user?.role || UserRole.EndUser}
        userId={user?.id || ''}
      />

      {/* Create Ticket Modal */}
      <CreateTicketModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleTicketCreated}
      />
    </div>
  );
}
